// Счета шага «Счета за товар и логистику» — единый источник для живого шага (InvoicesStep) и
// read-only сводки завершённого шага (InvoicesReadonly). Чистые функции (без React).
//
// Счёт за товар — при предоплате (prepaymentPercent > 0) ИЛИ когда бэк выпустил документ счёта за товар
// (PREPAYMENT_INVOICE_TEMPLATE/SIGNED — это и «Счёт на предоплату», и «Счёт на оплату» для постоплатных
// сделок). Сумма: при предоплате — prepaymentAmountWithoutNds; иначе — полная стоимость товара без НДС.
// Счёт за логистику — всегда; его сумма без НДС считается роль-независимо через logisticsTotalWithoutNds
// (см. dealPrice.ts), т.к. бэк отдельного без-НДС итога логистики не даёт. Все суммы в UI — БЕЗ НДС.
import { goodsTotalWithoutNds, logisticsTotalWithoutNds } from '@/lib/dealPrice';
import type { BargainDocument, BargainStepsInfo } from '@/types/deals';

export interface InvoiceView {
  readonly key: 'goods' | 'logistic';
  readonly titleKey: string;
  readonly amount: number | undefined;
  readonly doc: BargainDocument | null;
}

/** Выбираем подписанный документ счёта, иначе шаблон (то, что сгенерила 1С). */
export function pickInvoice(docs: readonly BargainDocument[], signedType: string, templateType: string): BargainDocument | null {
  return docs.find((d) => d.documentType === signedType) ?? docs.find((d) => d.documentType === templateType) ?? null;
}

/** Счёт подписан, если это SIGNED_-документ либо подпись агента в статусе DONE. */
export function isInvoiceSigned(doc: BargainDocument | null): boolean {
  return doc != null && (doc.documentType?.startsWith('SIGNED_') === true || doc.signatures?.AGENT?.state === 'DONE');
}

/** Список счетов сделки (счёт за товар + счёт за логистику) с суммами без НДС и документами. */
export function buildInvoices(info: BargainStepsInfo): InvoiceView[] {
  const docs = info.documents ?? [];
  const payment = info.payment ?? {};
  const hasPrepayment = (info.prepaymentPercent ?? 0) > 0;
  const goodsDoc = pickInvoice(docs, 'SIGNED_PREPAYMENT_INVOICE', 'PREPAYMENT_INVOICE_TEMPLATE');
  const logistic = pickInvoice(docs, 'SIGNED_LOGISTIC_INVOICE', 'LOGISTIC_INVOICE_TEMPLATE');
  // Счёт за товар показываем при предоплате или когда выпущен сам документ счёта (постоплата).
  const showGoods = hasPrepayment || goodsDoc != null;
  const goodsAmount = hasPrepayment ? payment.prepaymentAmountWithoutNds : goodsTotalWithoutNds(info);
  return [
    ...(showGoods ? [{ key: 'goods' as const, titleKey: 'app.deals.invoices.goods', amount: goodsAmount, doc: goodsDoc }] : []),
    { key: 'logistic' as const, titleKey: 'app.deals.invoices.logistic', amount: logisticsTotalWithoutNds(info), doc: logistic },
  ];
}

/** Счета, у которых есть что показать (сумма или документ) — для read-only сводки. */
export function visibleInvoices(info: BargainStepsInfo): InvoiceView[] {
  return buildInvoices(info).filter((inv) => inv.amount != null || inv.doc != null);
}
